//------------------------------------------------------------------------------
// BFA10103G 共架申込 機器設備 - 電柱一覧
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {
  FlexGrid,
  FlexGridColumn,
} from '@grapecity/wijmo.react.grid';
import { CellType } from '@grapecity/wijmo.grid';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import PoleItem from '@/utils/pole-item';
import MessageArea from '@/components/molecules/WarningMessageArea';

// 定数
const LOGH = 'BFA10103G pole list';

/**
 * 電柱一覧
 */
export default function PoleSelectList({
  warnings,
  poleList,
  orderPoles,
  itemSelected,
  onItemSelect,
  onSetubiAdd,
}) {

  // グリッドに渡すプロパティ (最新の値を ref 経由で渡す)
  const propValues = React.useRef();
  React.useEffect(() => {
    propValues.current = { onItemSelect };
  });

  // グリッド初期化処理
  const gridRef = React.useRef();
  const handleInitialize = React.useCallback((grid) => {
    console.debug(LOGH, 'グリッド初期化');

    // グリッド参照
    gridRef.current = grid;

    // 左上セルにタイトルを設定
    if (grid?.topLeftCells) {
      grid.topLeftCells.setCellData(0, 0, '電柱選択');
    }

    // 列ヘッダーのサイズ設定
    grid.autoSizeRow(0, true);

    // クリックハンドラー登録
    grid.removeEventListener(grid.hostElement, 'click');
    grid.addEventListener(
        grid.hostElement, 'click',
        (e) => handleListClick(grid, e, propValues));
  }, []);

  //----------------------------------------------------------------------------
  // 状態変更
  //----------------------------------------------------------------------------
  //
  // 電柱一覧変更
  //
  React.useEffect(() => {
    const grid = gridRef.current;
    if (grid) {

      // 一覧設定
      grid.itemsSource = poleList.map((pole) => ({
        ...pole,
        poleDisp: PoleItem.text(pole),
      }));

      // 選択解除
      grid.select(-1, -1);
    }
  }, [poleList]);

  //
  // 選択項目変更
  //
  React.useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    // 選択項目変更 - 電柱選択
    if (itemSelected?.pole) {
      const poleSelected = itemSelected.pole;
      const selectedFrom = itemSelected.from;
      console.debug(LOGH, '選択電柱変更', poleSelected);

      // 選択電柱に行頭アイコンを表示
      applyPoleSelect(grid, orderPoles, poleSelected, selectedFrom);
    }

    // 選択項目変更 - 選択解除
    else {
      applyPoleSelect(grid, orderPoles, null);
    }

    // グリッド再描画
    grid.invalidate(false);
  }, [poleList, orderPoles, itemSelected]);

  //----------------------------------------------------------------------------
  // イベント
  //----------------------------------------------------------------------------
  //
  // 設備追加ボタン
  //
  const handleSetubiAdd = React.useCallback((e, ctx) => {
    console.info(LOGH, '設備追加', PoleItem.toString(ctx.item));
    onSetubiAdd(ctx.item);
  }, [onSetubiAdd]);

  //----------------------------------------------------------------------------
  // 描画
  //----------------------------------------------------------------------------
  return (
    <Stack direction="column"
      sx={{
        height: '100%',
        width: 'auto',
      }}
    >
      {/* メッセージ */}
      <MessageArea messages={warnings} />

      {/* 電柱一覧 */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flexGrow: 1, minHeight: 0,  // 下段の残り全部

          // ヘッダー文字列を中央に配置
          '& .wj-cell.wj-header': {
            padding: 1,
            whiteSpace: 'pre-line',  // 折り返して表示
          },

          // 選択行の背景を変えない
          '& .wj-cells .wj-cell.wj-state-multi-selected': {
            color: 'inherit',
            bgcolor: 'white',
          },
          '& .wj-cells .wj-cell.wj-state-multi-selected.wj-alt': {
            bgcolor: '#F7F7F7',
          },

          // 行頭セル - クリッカブル
          '& .wj-rowheaders .wj-header': { cursor: 'pointer' },

          // 行頭の編集アイコン(鉛筆アイコン)を消す - 読み取り専用なので不要
          // '& .wj-rowheaders .wj-glyph-pencil': { display: 'none' },

          // 選択電柱の行頭にアイコンを描画
          '& .wj-rowheaders .wj-header.pole-selected': {
            backgroundImage: 'url("/icons/check-24x24.png")',
            backgroundPositionX: '4px',
            backgroundPositionY: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '20px',
          },

          // 申込電柱の行頭にアイコンを表示
          '& .wj-rowheaders .wj-header.pole-order': {
            backgroundImage: 'url("/icons/square-red.svg")',
            backgroundPositionX: '26px',
            backgroundPositionY: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '14px',
          },

          // 選択電柱で申込電柱なら両方のアイコンを表示
          '& .wj-rowheaders .wj-header.pole-selected.pole-order': {
            backgroundImage: [
              'url("/icons/check-24x24.png")',
              'url("/icons/square-red.svg")',
            ].join(','),
            backgroundPositionX: '4px, 26px',
            backgroundSize: '20px, 14px',
          },

          // 設備追加セル - アイコンボタン
          '& .wj-cell.setubi-add button': {
            border: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          },

          // 設備追加セル - アイコン設定
          '& .wj-cell.setubi-add .setubi-add-icon': {
            cursor: 'pointer',
            display: 'inline-block',
            width: '24px',
            height: '24px',
            backgroundColor: 'grey',
            maskSize: 'contain',
            maskImage: 'url("/icons/add-circle-fill.svg")',
            maskRepeat: 'no-repeat',
          },

          // 設備追加セル - ホバーで着色して少し大きめにする
          '& .wj-cell.setubi-add button:hover .setubi-add-icon': {
            backgroundColor: 'primary.main',
            width: '30px',
            height: '30px',
          },
        }}
      >
        {/* 電柱一覧グリッド */}
        <FlexGrid
          isReadOnly
          // selectionMode="Row"
          showMarquee
          alternatingRowStep
          showSelectedHeaders="Row"
          style={{ height: '100%' }}
          initialized={handleInitialize}
        >
          {/* 電柱属性 - 地区/線路/電柱番号/電柱区分 */}
          <FlexGridColumn binding="areaName" header="地区"     width={80}/>
          <FlexGridColumn binding="lineName" header="冠称"     width={90} />
          <FlexGridColumn binding="poleDisp" header="電柱番号" width={100} />
          <FlexGridColumn binding="poleKind" header="施設名"   width={80} />

          {/* 設備追加 - アイコンボタン */}
          <FlexGridColumn
            header="設備追加"
            width={48}
            cssClass="setubi-add"
            allowSorting={false}
            cellTemplate={CellMaker.makeButton({
              text: '<span class="setubi-add-icon" />',
              click: handleSetubiAdd,
            })}
          />
        </FlexGrid>
      </Box>
    </Stack>
  );
}

// プロパティ
PoleSelectList.propTypes = {
  warnings:     PropTypes.array.isRequired,     // 警告メッセージ
  poleList:     PropTypes.array.isRequired,     // 電柱一覧
  orderPoles:   PropTypes.instanceOf(Map),      // 申込電柱一覧
  itemSelected: PropTypes.object,               // 選択電柱

  // イベントハンドラー
  onItemSelect: PropTypes.func.isRequired,      // 電柱選択
  onSetubiAdd:  PropTypes.func.isRequired,      // 設備追加
};

//
// グリッド項目クリック処理
//
function handleListClick(grid, e, propValues) {
  const { onItemSelect } = propValues.current;

  // ヒットテスト
  const hit = grid.hitTest(e);

  // 行頭セルクリック - 電柱選択
  if (hit.cellType === CellType.RowHeader) {
    const pole = grid.rows[hit.row].dataItem;
    const selectedPole = new PoleItem(pole);
    console.info(LOGH, '電柱選択', selectedPole?.toString());

    // 選択電柱更新
    onItemSelect(selectedPole, { from: 'list' });
  }
}

//
// 選択電柱表示反映 - 行頭アイコン表示
//
function applyPoleSelect(grid, orderPoles, poleSelected, selectedFrom) {

  // 選択電柱と同じ電柱を探す
  let rowSelected = null;
  grid.rows.forEach((row) => {
    let cssSelected = null;

    // 同一電柱判定
    const pole = row.dataItem;
    if (poleSelected && poleSelected.same(pole)) {
      rowSelected = row;
      cssSelected = 'pole-selected';
    }

    // 申込電柱判定
    const key = PoleItem.key(pole);
    const cssOrder = orderPoles.has(key)
        ? 'pole-order'
        : null;

    // 選択行にCSSクラスを設定
    row.cssClassAll = [ cssSelected, cssOrder ]
        .filter(Boolean)
        .join(' ') || null;
  });

  // 選択行が表示範囲外ならスクロール
  if (rowSelected && selectedFrom !== 'list') {
    const range = grid.viewRange;
    const index = rowSelected.index;
    if (!range.contains(index, 0)) {
      grid.scrollIntoView(index, -1);
    }
  }
}
